/**
 * Pricing Page Component
 * 
 * Main pricing page with plans, features, and subscription management.
 */

import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import Layout from '../../components/Layout';
import PricingTable from '../../components/subscription/PricingTable';
import { useSubscription } from '../../context/subscription/SubscriptionContext';
import { useAuth } from '../../context/AuthContext';

const PricingPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const { 
    plans, 
    currentSubscription, 
    loading, 
    error, 
    refreshSubscription 
  } = useSubscription();

  // Handle URL parameters for success/error states
  useEffect(() => {
    const success = searchParams.get('success');
    const cancelled = searchParams.get('cancelled');
    const error = searchParams.get('error');

    if (success === 'true') {
      // Handle successful payment
      refreshSubscription();
      // Clear URL params
      navigate('/subscription/pricing', { replace: true });
    } else if (cancelled === 'true') {
      // Handle cancelled checkout
      // Could show a message here
      navigate('/subscription/pricing', { replace: true });
    } else if (error) {
      // Handle checkout error
      console.error('Checkout error:', error);
      navigate('/subscription/pricing', { replace: true });
    }
  }, [searchParams, navigate, refreshSubscription]);

  const handlePlanSelect = async (planId: string) => {
    if (!isAuthenticated) {
      // Redirect to auth with return URL
      navigate(`/auth?redirect=${encodeURIComponent('/subscription/pricing')}&plan=${planId}`);
      return;
    }

    // If already authenticated, proceed with checkout
    // Don't provide onPlanSelect so PricingTable uses its default startCheckout behavior
  };

  if (!isAuthenticated) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          {/* Hero Section for Unauthenticated Users */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Powerful AI Audience Research
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Get deep insights into your target audience with AI-powered simulations. 
              Test campaigns, analyze behavior, and make data-driven decisions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/auth?mode=signup')}
                className="px-8 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors"
              >
                Start Free Trial
              </button>
              <button
                onClick={() => navigate('/auth?mode=login')}
                className="px-8 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              >
                Sign In
              </button>
            </div>
          </div>

          {/* Pricing Table */}
          <PricingTable
            plans={plans}
            onPlanSelect={handlePlanSelect}
            showCurrentPlan={false}
          />

          {/* Benefits Section */}
          <div className="mt-20">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
              Why Choose Our Platform?
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-3">AI-Powered Insights</h3>
                <p className="text-gray-600">
                  Get accurate audience insights powered by advanced AI models 
                  that understand human behavior and preferences.
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Real-Time Testing</h3>
                <p className="text-gray-600">
                  Test campaigns and content in real-time with virtual audiences 
                  before launching to real customers.
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Data-Driven Decisions</h3>
                <p className="text-gray-600">
                  Make confident marketing decisions backed by comprehensive 
                  data and behavioral analysis.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        {/* Header for Authenticated Users */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Subscription Plans
          </h1>
          <p className="text-gray-600">
            Choose the plan that best fits your needs. You can upgrade or downgrade at any time.
          </p>
        </div>

        {/* Current Subscription Status */}
        {currentSubscription && (
          <div className="mb-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-blue-600 mr-3" />
              <div>
                <p className="font-medium text-blue-900">
                  Current Plan: {currentSubscription.name}
                </p>
                <p className="text-sm text-blue-700">
                  {currentSubscription.status === 'active' 
                    ? currentSubscription.endDate 
                      ? `Active until ${new Date(currentSubscription.endDate).toLocaleDateString()}`
                      : 'Active'
                    : `Status: ${currentSubscription.status}`
                  }
                </p>
              </div>
            </div>
            
            {currentSubscription.cancelAtPeriodEnd && (
              <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
                <div className="flex items-center">
                  <AlertTriangle className="w-4 h-4 text-yellow-600 mr-2" />
                  <p className="text-sm text-yellow-800">
                    Your subscription will be cancelled at the end of the current period.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <XCircle className="w-5 h-5 text-red-600 mr-3" />
              <p className="text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* Pricing Table */}
        <PricingTable
          plans={plans}
          currentPlanId={currentSubscription?.id}
          showCurrentPlan={true}
        />
      </div>
    </Layout>
  );
};

export default PricingPage;